import * as React from "react"
import { CircleAlert, CircleCheck, CloudUpload, FileText, RotateCcw, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { getErrorMessage } from "@/lib/errors"
import { describeAccept, fileMatchesAccept, formatFileSize } from "@/lib/upload"
import { ProgressBar } from "@/components/ui/progress-bar"

/*
  FileDropzone — drop files (or browse / take a photo on a phone) and watch each upload's progress
  (06 #7 upload into a folder, 05 #10 DN photo, 09 #8 site photos, 16 upload).

    <FileDropzone
      accept=".pdf,.dwg,image/*" multiple maxSize={25 * 1024 * 1024}
      upload={(file, { onProgress, signal }) => uploadFile(`/projects/${id}/files`, file, { fields: { folder }, onProgress, signal })}
      onUploaded={(result, file) => setFiles((list) => [result, ...list])}
      label="Drop files into c. Variations" />

  - Wrong type / too big / too many files are refused at once with the reason (the server checks again).
  - Each file: name · size · progress bar → "Uploaded" ✓, or the error with Try again; Cancel while uploading.
  - At most `concurrency` (3) uploads run at once — 30 site photos over 4G go 3 by 3; the rest show "Waiting…".
  - With `multiple={false}` a new file replaces the old one and cancels its upload if it was still running.
  - Without `upload` the zone only collects files: `onFilesChange(files)` gets the accepted File objects.
  Props: accept (input accept string or array), multiple (true), maxSize (bytes), maxFiles, upload(file,
  { onProgress, signal }) → Promise<result>, onUploaded(result, file), onFilesChange(files), concurrency (3),
  capture ('environment' opens the camera on phones), label, hint, disabled, compact, className,
  testId ('file-dropzone').
*/
let nextId = 0

function FileDropzone({
  accept,
  multiple = true,
  maxSize,
  maxFiles,
  upload,
  onUploaded,
  onFilesChange,
  capture,
  concurrency = 3,
  label = "Drop files here or browse",
  hint,
  disabled = false,
  compact = false,
  className,
  testId = "file-dropzone",
}) {
  const inputRef = React.useRef(null)
  const controllers = React.useRef(new Map()) // entry id → AbortController of a running upload
  const queue = React.useRef([]) // entries waiting for a free upload slot
  const running = React.useRef(0)
  const alive = React.useRef(true)
  const latest = React.useRef({ upload, onUploaded, concurrency })
  const [entries, setEntries] = React.useState([])
  const [over, setOver] = React.useState(false)
  const hintId = React.useId()

  React.useLayoutEffect(() => {
    latest.current = { upload, onUploaded, concurrency }
  })

  const patch = (id, change) => setEntries((current) => current.map((entry) => (entry.id === id ? { ...entry, ...change } : entry)))

  const runUpload = async (entry) => {
    const controller = new AbortController()
    controllers.current.set(entry.id, controller)
    patch(entry.id, { status: "uploading", progress: 0, error: "" })
    try {
      const result = await latest.current.upload(entry.file, {
        signal: controller.signal,
        onProgress: (pct) => patch(entry.id, { progress: pct }),
      })
      if (!alive.current) return
      if (controller.signal.aborted) throw new Error("aborted") // an upload function that ignored the signal
      patch(entry.id, { status: "done", progress: 100, result })
      latest.current.onUploaded?.(result, entry.file)
    } catch (error) {
      if (!alive.current) return
      if (controller.signal.aborted) patch(entry.id, { status: "cancelled", error: "Upload cancelled" })
      else patch(entry.id, { status: "error", error: getErrorMessage(error, "Upload failed") })
    } finally {
      controllers.current.delete(entry.id)
    }
  }

  // Start queued uploads while a slot is free; each finished upload frees its slot and pumps again.
  const pump = () => {
    const limit = Math.max(1, Number(latest.current.concurrency) || 1)
    while (alive.current && latest.current.upload && running.current < limit && queue.current.length) {
      const entry = queue.current.shift()
      running.current += 1
      runUpload(entry).finally(() => {
        running.current -= 1
        pump()
      })
    }
  }

  const enqueue = (list) => {
    if (!upload || !list.length) return
    queue.current.push(...list)
    pump()
  }

  const retry = (entry) => {
    patch(entry.id, { status: "queued", progress: 0, error: "" })
    enqueue([entry])
  }

  React.useEffect(() => {
    // The Map itself never changes (uploads are added to / removed from it), so the cleanup can hold it directly.
    const running = controllers.current
    alive.current = true
    return () => {
      alive.current = false
      queue.current = []
      running.forEach((controller) => controller.abort())
    }
  }, [])

  const addFiles = (fileList) => {
    if (disabled) return
    const files = Array.from(fileList || [])
    if (!files.length) return
    const accepted = []
    const added = []
    const alreadyKept = entries.filter((entry) => entry.status !== "rejected").length
    files.forEach((file, index) => {
      let error = ""
      if (!multiple && index > 0) error = "Only one file can be added here"
      else if (!fileMatchesAccept(file, accept)) error = `This type of file is not accepted${accept ? ` (${describeAccept(accept)})` : ""}`
      else if (maxSize && file.size > maxSize) error = `Too big — the limit is ${formatFileSize(maxSize)}`
      else if (maxFiles && alreadyKept + accepted.length >= maxFiles) error = `At most ${maxFiles} files`
      nextId += 1
      const entry = { id: `f${nextId}`, file, status: error ? "rejected" : upload ? "queued" : "added", progress: 0, error }
      added.push(entry)
      if (!error) accepted.push(entry)
    })
    if (!multiple) {
      // the new file replaces the old one: stop whatever the old one was still doing
      queue.current = []
      controllers.current.forEach((controller) => controller.abort())
    }
    setEntries((current) => (multiple ? [...current, ...added] : added))
    if (onFilesChange) onFilesChange(accepted.map((entry) => entry.file))
    enqueue(accepted)
  }

  const remove = (entry) => {
    queue.current = queue.current.filter((item) => item.id !== entry.id)
    controllers.current.get(entry.id)?.abort()
    setEntries((current) => current.filter((item) => item.id !== entry.id))
  }

  const describedHint = hint || [accept ? describeAccept(accept) : "", maxSize ? `up to ${formatFileSize(maxSize)}` : ""].filter(Boolean).join(" · ")

  return (
    <div className={cn("flex flex-col gap-2", className)} data-testid={testId}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-describedby={describedHint ? hintId : undefined}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(event) => {
          if ((event.key === "Enter" || event.key === " ") && !disabled) {
            event.preventDefault()
            inputRef.current?.click()
          }
        }}
        onDragOver={(event) => {
          if (disabled) return
          event.preventDefault()
          event.dataTransfer.dropEffect = "copy"
          setOver(true)
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setOver(false)
        }}
        onDrop={(event) => {
          event.preventDefault()
          setOver(false)
          addFiles(event.dataTransfer?.files)
        }}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-card border-2 border-dashed text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          compact ? "px-4 py-3" : "flex-col justify-center px-6 py-7 text-center",
          over ? "border-brand bg-brand-soft" : "border-[color:var(--line-dashed)] bg-surface-2 hover:border-[color:var(--brand-soft-border)] hover:bg-surface",
          disabled && "cursor-not-allowed opacity-60"
        )}
        data-testid={`${testId}-zone`}
        data-over={over ? "true" : undefined}
      >
        <span className="ui-icon-tile tone-navy h-10 w-10 rounded-xl" aria-hidden="true">
          <CloudUpload className="h-5 w-5" />
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[14px] font-semibold text-ink">{over ? "Drop to add" : label}</span>
          {describedHint ? <span id={hintId} className="text-[12.5px] text-ink-3">{describedHint}</span> : null}
        </span>
      </div>
      {/* a sibling of the zone, not inside it: no interactive content inside role="button" */}
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        accept={Array.isArray(accept) ? accept.join(",") : accept}
        multiple={multiple}
        capture={capture}
        disabled={disabled}
        onChange={(event) => {
          addFiles(event.target.files)
          event.target.value = ""
        }}
        data-testid={`${testId}-input`}
      />
      {entries.length ? (
        <ul className="flex flex-col gap-1.5" aria-label="Files" aria-live="polite">
          {entries.map((entry) => {
            const failed = entry.status === "error" || entry.status === "rejected" || entry.status === "cancelled"
            return (
              <li
                key={entry.id}
                className={cn(
                  "flex items-center gap-3 rounded-control border px-3 py-2",
                  failed ? "border-[color:var(--danger-border)] bg-status-red-bg" : "border-line bg-surface"
                )}
                data-testid={`${testId}-file`}
                data-status={entry.status}
              >
                <FileText className={cn("h-5 w-5 shrink-0", failed ? "text-status-red-ink" : "text-ink-3")} aria-hidden="true" />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate text-[13px] font-semibold text-ink">{entry.file.name}</span>
                    <span className="shrink-0 text-[12px] text-ink-3">{formatFileSize(entry.file.size)}</span>
                  </div>
                  {entry.status === "uploading" ? (
                    entry.progress === null || entry.progress === undefined ? (
                      // progress unknown (no upload events): an indeterminate bar
                      <span role="progressbar" aria-label={`Uploading ${entry.file.name}`} className="ui-skeleton-shimmer block h-1.5 w-full rounded-full" />
                    ) : (
                      <ProgressBar value={entry.progress} label={`Uploading ${entry.file.name}`} className="w-full" />
                    )
                  ) : null}
                  {entry.status === "done" ? (
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-status-green-ink">
                      <CircleCheck className="h-3.5 w-3.5" aria-hidden="true" />Uploaded
                    </span>
                  ) : null}
                  {entry.status === "queued" ? <span className="text-[12px] text-ink-3">Waiting…</span> : null}
                  {failed ? (
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-status-red-ink" role="alert">
                      <CircleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />{entry.error}
                    </span>
                  ) : null}
                </div>
                {(entry.status === "error" || entry.status === "cancelled") && upload ? (
                  <button
                    type="button"
                    onClick={() => retry(entry)}
                    className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-[12.5px] font-semibold text-brand-ink hover:bg-surface"
                    data-testid={`${testId}-retry`}
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />Try again
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => remove(entry)}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={entry.status === "uploading" ? `Cancel uploading ${entry.file.name}` : `Remove ${entry.file.name}`}
                  data-testid={`${testId}-remove`}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

export { FileDropzone }
